/**
 * Paragraph Skeleton Pattern
 *
 * A skeleton loader specifically designed for paragraph elements.
 * Provides appropriate sizing and styling for paragraph text.
 */
import type { FC } from 'react';
import { Skeleton } from '../index';

export interface ParagraphSkeletonProps {
	/**
	 * Width of the skeleton (CSS value)
	 * @default "100%" - Full width for paragraph text
	 */
	width?: string;

	/**
	 * Optional aria message for screen readers
	 */
	ariaMessage?: string;

	/**
	 * Custom class name
	 */
	className?: string;

	/**
	 * Number of lines to show
	 * @default 1
	 */
	lines?: number;
}

/**
 * Paragraph skeleton component
 *
 * Renders a skeleton loader that matches the size and appearance of a paragraph element.
 * Supports multiple lines with the last line being slightly shorter for a natural look.
 */
export const ParagraphSkeleton: FC<ParagraphSkeletonProps> = ({
	width = '100%',
	ariaMessage = 'Loading paragraph...',
	className = '',
	lines = 1,
}) => {
	// For single line, use full width
	if (lines === 1) {
		return (
			<Skeleton
				width={width}
				height="1em"
				ariaMessage={ariaMessage}
				className={className}
			/>
		);
	}

	// For multiple lines, render them with spacing
	return (
		<div className={className} style={{ display: 'flex', flexDirection: 'column', gap: '0.5em' }}>
			{Array.from({ length: lines }).map((_, index) => (
				<Skeleton
					key={index}
					width={index === lines - 1 ? '80%' : '100%'}
					height="1em"
					ariaMessage={index === 0 ? ariaMessage : undefined}
				/>
			))}
		</div>
	);
};
